import { useCallback } from "react";

import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";

import { showErrorToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import type {
    IScoringRuleDto,
    ScoringConfigRule,
    ScoringConfigType,
} from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { buildFilterTitleByKey } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/buildFilterTitleByKey";
import { useTeamsListQuery } from "app/pages/sales-intelligence/modules/teams/queries/useTeamsListQuery";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { SignalByIdMap } from "app/pages/sales-intelligence/sub-modules/signals/types/extended-models";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { errorCategoryMap, SiError } from "app/pages/sales-intelligence/utils/errors/types";

import { SCORING_EXPORT_TRANSLATION_KEYS } from "../constants/translations";
import { buildScoringRulesExportRows } from "../utils/buildScoringRulesExportRows";

import { useScoringBoardCrud } from "./useScoringBoardCrud";

/** Matches the "Date Downloaded" cell of the approved report template. */
const EXPORT_DATE_FORMAT = "DD.MM.YY";

type UseExportScoringRulesParams = {
    /** The rules of the active segment — the export mirrors what the board currently shows. */
    readonly rules: ScoringConfigRule;
    readonly getRuleScope: (rule: IScoringRuleDto) => ScoringConfigType | undefined;
    readonly filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    readonly signalById: SignalByIdMap;
};

/**
 * Turns the board into an xlsx download: rows are built and translated here, the backend only writes
 * them into the report template.
 */
export const useExportScoringRules = ({
    rules,
    getRuleScope,
    filtersList,
    signalById,
}: UseExportScoringRulesParams) => {
    const dispatch = useDispatch();
    const { showSuccess } = useScoringBoardCrud();
    const { data: teams } = useTeamsListQuery();

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            const result = await salesApiService.signals.exportScoringConfigToExcel({
                userEmail: swSettings.user.username,
                date: dayjs().format(EXPORT_DATE_FORMAT),
                rules: buildScoringRulesExportRows({
                    rules,
                    getRuleScope,
                    titleByKey: buildFilterTitleByKey(filtersList),
                    signalById,
                    teams: teams ?? [],
                }),
            });

            // The download client reports failures on the result instead of rejecting.
            if (!result.success) {
                throw new SiError(
                    "Failed to export the scoring rules to Excel",
                    { category: errorCategoryMap.api },
                    { cause: result.error },
                );
            }
        },
        onSuccess() {
            showSuccess(SCORING_EXPORT_TRANSLATION_KEYS.successToast);
        },
        onError(error) {
            dispatch(showErrorToast(translate(SCORING_EXPORT_TRANSLATION_KEYS.errorToast)));
            trackError(
                error instanceof SiError
                    ? error
                    : new SiError(
                          "Failed to export the scoring rules to Excel",
                          { category: errorCategoryMap.api },
                          { cause: error },
                      ),
            );
        },
    });

    const exportRules = useCallback(() => mutate(), [mutate]);

    return { exportRules, isExporting: isPending };
};
